import React, { useEffect, useState } from 'react';

import TextField from '@material-ui/core/TextField';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { useForm, SubmitHandler, Controller } from 'react-hook-form';
import { makeStyles } from '@material-ui/core/styles';
import { InputLabel } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import { MerchantOfferEdit, Offer } from '../../types/offer';
import { DateTime } from 'luxon';
import { getMerchantDataPasses, getTags, updateOffer } from '../../services/c2dcrm';
import { config } from '../../configs/config';
import COLOURS from '../../constants/colour';
import { DataPass } from '../../types/dataPassport';
import ReactSelect, { ActionMeta } from 'react-select';
import { ContactSupportOutlined } from '@material-ui/icons';


type EditOfferForm = {
  title: string;
  details: string;
  start_date: string;
  end_date: string | null;
  offer_url: string;
  id: string;
  status: string;
  offer_image_url: string;
  data_passes: string[];
  tags: string[];
};

type Props = {
  offer: MerchantOfferEdit;
  token: string;
  closeModal: () => void;
  fakeUpdateState: (offer: MerchantOfferEdit) => void;
};

function formatDate(s: string): string {
  const r = DateTime.fromISO(s).toFormat('yyyy-MM-dd');

  if (r === 'Invalid DateTime') {
    return ('no ending date')
  }
  else {
    return (r);
  }
}

type StateType = {
  passes: DataPass[];
  loading: boolean;
};

export interface TagOption {
  readonly value: string;
  readonly label: string;
}

export interface DataPassOption {
  readonly value: string;
  readonly label: string;
}

const MerchantPerkEditForm: React.FC<Props> = ({
  offer,
  token,
  closeModal,
  fakeUpdateState
}) => {

  const classes = useStyles();

  const {
    register,
    handleSubmit,
    setValue,
    control,
    formState
  } = useForm<EditOfferForm>();

  const { dirtyFields, errors } = formState;

  const [tags, setTags] = useState<string[]>([]);
  const [defaultTags, setDefaultTags] = useState<TagOption[]>([]);
  const [tagOptions, setTagOptions] = useState<TagOption[]>([]);

  const [dataPassports, setDataPassports] = useState<StateType>({ passes: [], loading: false });
  const [dataPassOptions, setDataPassOptions] = useState<DataPassOption[]>([]);
  const [defaultDataPasses, setDefaultDataPasses] = useState<DataPassOption[]>([]);

  function findPassName(passId: string): string {    
    const a = dataPassports.passes.find(p => p.id === passId);

    if (a) {
      return `(${a?.name}) - ${a?.title}`;
    }
    else {
      return passId;
    }
  }

  function chooseDefaultDataPasses(): DataPassOption[] {
    const d = offer.data_passes.map((pass) => {
      return {
        value: pass,
        label: findPassName(pass)
      }
    });

    return d;
  }

  function chooseDefaultTags(): TagOption[] {
    const d = offer.tags.map((tag) => {
      return { value: tag, label: tag }
    });

    return d;
  }

  function updateState(offer: MerchantOfferEdit, data: EditOfferForm) {
    offer.title = data.title;
    offer.start_date = data.start_date;
    offer.end_date = (data.end_date ? data.end_date : null);
    offer.offer_image_url = data.offer_image_url;
    offer.data_passes = data.data_passes;
    offer.tags = data.tags;
    offer.offer_url = data.offer_url;
    offer.status = data.status;
  }

  const onSubmit: SubmitHandler<EditOfferForm> = (data: EditOfferForm): void => {
    if (Object.keys(dirtyFields).length === 0) {
      closeModal();
    }
    else {
      data.start_date = new Date(data.start_date).toISOString();
      if (data.end_date) {
        data.end_date = new Date(data.end_date).toISOString();
      }
      else {
        data.end_date = null;
      }

      data.status = 'pending_approval';
      data.id = offer.id;
      data.offer_image_url = offer.offer_image_url;
    
      updateOffer(token, data).then((response) => {
        if (response.status === 200) {
          updateState(offer, data);
          closeModal();
        }
      
      });
    }
  };

  useEffect(() => {
    const fetchTags = async () => {
      setTags([]);
      try {
        getTags(token).then((response) => {
          setTags(response.data);
          const options = response.data.map((tag) => {
            return { value: tag, label: tag }
          });
          setTagOptions(options);
        })
      } catch (error) {
        setTags([]);
        setTagOptions([]);
      }
    };

    fetchTags();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setDataPassports({ passes: [], loading: true });
      try {
        getMerchantDataPasses(token).then((response) => {
          setDataPassports({ passes: response.data, loading: false });

          const options = response.data.map((pass) => {
            return { value: pass.id, label: pass.name }
          });
          setDataPassOptions(options);
        })
      } catch (error) {
        setDataPassports({ passes: [], loading: false });
        setDataPassOptions([]);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const defDPOptions = offer.data_passes.map((pass) => {
      return { value: pass, label: pass }
    });
    setDefaultDataPasses(defDPOptions);

    const defTOptions = offer.tags.map((tag) => {
      return { value: tag, label: tag }
    });
    setDefaultTags(defTOptions);

    setValue("data_passes", offer.data_passes);
    setValue("tags", offer.tags);
  }, [dataPassports.loading]);

  const dataPassHandleChange = (
    options: readonly DataPassOption[],
    actionMeta: ActionMeta<DataPassOption>) => {
    
    setValue("data_passes", options.map((item) => item.value), { shouldDirty: true });
  }

  const tagHandleChange = (
    options: readonly TagOption[],
    actionMeta: ActionMeta<TagOption>) => {
    
    setValue("tags", options.map((item) => item.value), { shouldDirty: true });
  }

  if (dataPassports.loading) {
    return <div className={classes.container}>Loading...</div>
  }
  else {
    return (
      <div className={classes.container}>
        <Card style={{ minWidth: 275, borderRadius: '8px' }}>
          <Typography style={{ textAlign: 'center' }} variant="h4">
            Edit Your Offer
          </Typography>

          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid
                container
                alignItems="center"
                justifyContent="center"
                spacing={4}
              >
                <Grid item xs={12}>
                  <Controller
                    name="data_passes"
                    control={control}
                    render={({ field }) =>
                      <ReactSelect
                        defaultValue={chooseDefaultDataPasses()}
                        getOptionLabel={(opt: DataPassOption) => opt.label}
                        getOptionValue={(opt: DataPassOption) => opt.value}
                        options={dataPassOptions}
                        onChange={dataPassHandleChange}
                        isMulti
                        isClearable
                      />
                    }
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    key="1"
                    id="title"
                    style={{ width: '100%' }}
                    label="Offer Title"
                    defaultValue={offer.title}
                    {...register('title', { required: true })}
                  />
                </Grid>

                <Grid item xs={12}>
                  <InputLabel id="demo-mutiple-name-label">Tags</InputLabel>
                  <Controller
                    name="tags"
                    control={control}
                    render={({ field }) =>
                      <ReactSelect
                        defaultValue={chooseDefaultTags()}
                        getOptionLabel={(opt: TagOption) => opt.label}
                        getOptionValue={(opt: TagOption) => opt.value}
                        options={tagOptions}
                        onChange={tagHandleChange}
                        isMulti
                        isClearable
                      />
                    }
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    multiline
                    id="details"
                    defaultValue={offer.details}
                    label="Offer Details"
                    style={{ width: '100%' }}
                    {...register('details', { required: true })}
                  />
                  {errors.details && <span>This field is required</span>}
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    id="start_date"
                    label="Start Date"
                    type="date"
                    defaultValue={formatDate(offer.start_date)}
                    style={{ width: '100%' }}
                    {...register('start_date', { required: true })}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />{' '}
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    id="end_date"
                    label="End Date"
                    type="date"
                    defaultValue={offer.end_date !== null ? formatDate(offer.end_date) : ''}
                    style={{ width: '100%' }}
                    {...register('end_date')}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />{' '}
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    key="url"
                    id="url"
                    label="URL for more details"
                    defaultValue={offer.offer_url}
                    className={classes.inputWrapper}
                    {...register('offer_url', { required: true })}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Button
                    style={{ color: 'white', backgroundColor: config.merchantColour }}
                    type="submit">
                    Update Offer
                  </Button>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Button
                    style={{ color: 'white', backgroundColor: config.merchantColour }}
                    type="button" onClick={() => closeModal()}>
                    Cancel
                  </Button>
                </Grid>

              </Grid>
            </form>
          </CardContent>
        </Card>
      </div>
    )
  }
};

const useStyles = makeStyles(() => ({
  container: {
    display: 'grid',
    gridGap: '2px',
    placeItems: 'center',
    textAlign: 'center',
    marginBottom: '0px',
    minWidth: '248px',
    boxShadow: '0px 5px 26px rgba(0, 0, 0, 0.2)',
    backgroundColor: '#FFFFFF',
    width: '80%',
  },
  codeText: {
    marginTop: '8px',
  },
  infoRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    color: COLOURS.MediumGrey,

    '& img': {
      height: '18px',
      marginRight: '8px',
    },
  },
  info: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  leftText: {
    alignItems: 'left',
    textAlign: 'left',
  },
  leftTextMuted: {
    alignItems: 'left',
    textAlign: 'left',
    color: 'grey',
    fontWeight: 'normal',
  },
  inputWrapper: {
    width: '100%',
    minHeight: '80px',
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  chip: {
    margin: 2,
  },
}));

export default MerchantPerkEditForm;
